import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCMS } from '@/hooks/useCMS';

const DEFAULT = {
  label: 'პროდუქტები',
  heading: 'კატეგორიები',
  all_link_label: 'ყველა პროდუქტი',
  all_link_href: '/products',
  services: [
    { num: '01', title: 'კერამიკა', brand: 'Laminam', slug: 'ceramics', desc: 'ულტრა-თხელი დიდი ფორმატის კერამიკული ფილები.', image: 'https://readdy.ai/api/search-image?query=Large%20format%20ceramic%20tiles%20luxury%20interior&width=900&height=600&seq=svc1&orientation=landscape' },
    { num: '02', title: 'იატაკი', brand: 'Fodbo · Dicson', slug: 'flooring', desc: 'ლამინირებული პარკეტი, ინჟინერული ხე.', image: 'https://readdy.ai/api/search-image?query=Luxury%20wood%20parquet%20flooring&width=900&height=600&seq=svc2&orientation=landscape' },
    { num: '03', title: 'ფრიალი იატაკი', brand: 'Ardex · Tecnopol', slug: 'resin-flooring', desc: 'ეპოქსიდური და პოლიურეთანის სისტემები.', image: 'https://readdy.ai/api/search-image?query=Polished%20epoxy%20resin%20floor&width=900&height=600&seq=svc3&orientation=landscape' },
    { num: '04', title: 'ჭერი', brand: 'Armstrong · Knauf', slug: 'ceiling', desc: 'გაჭიმული ჭერის სისტემები.', image: 'https://readdy.ai/api/search-image?query=Modern%20suspended%20ceiling%20system&width=900&height=600&seq=svc4&orientation=landscape' },
    { num: '05', title: 'ფასადი', brand: 'Laminam · Tecnopol', slug: 'facade', desc: 'გარე ფასადის სისტემები.', image: 'https://readdy.ai/api/search-image?query=Modern%20building%20exterior%20facade&width=900&height=600&seq=svc5&orientation=landscape' },
    { num: '06', title: 'სამშენებლო ქიმია', brand: 'Ardex · Tecnopol', slug: 'construction-chemistry', desc: 'წებოები, შემმავსებლები, ჰიდროიზოლაცია.', image: 'https://readdy.ai/api/search-image?query=Professional%20construction%20chemicals&width=900&height=600&seq=svc6&orientation=landscape' },
  ],
};

export default function ServicesPreview() {
  const [hovered, setHovered] = useState<number | null>(null);
  const [activeImg, setActiveImg] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const d = useCMS('services_preview', DEFAULT);
  const services: any[] = d.services || DEFAULT.services;

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('animate-fade-up'); observer.unobserve(entry.target); }
      });
    }, { threshold: 0.05 });
    const els = sectionRef.current?.querySelectorAll('.reveal');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-white py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-8 md:px-14">
        <div className="reveal opacity-0 flex items-end justify-between mb-16 md:mb-20 border-b border-[#1C1C1C]/8 pb-8">
          <div>
            <span className="text-[10px] font-bold tracking-[0.35em] uppercase text-[#ec3145] block mb-3">{d.label}</span>
            <h2 className="font-serif text-4xl md:text-6xl font-bold text-[#1C1C1C] leading-tight">{d.heading}</h2>
          </div>
          <Link to={d.all_link_href} className="hidden md:inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.2em] uppercase text-[#1C1C1C]/50 hover:text-[#ec3145] transition-colors duration-300 cursor-pointer whitespace-nowrap">
            {d.all_link_label} <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div className="reveal opacity-0">
            {services.map((s, i) => (
              <Link key={i} to={`/category/${s.slug}`} className="group flex items-center gap-6 py-5 border-b border-[#1C1C1C]/8 cursor-pointer"
                onMouseEnter={() => { setHovered(i); setActiveImg(i); }} onMouseLeave={() => setHovered(null)}>
                <span className="font-serif text-xs text-[#1C1C1C]/20 group-hover:text-[#ec3145] transition-colors duration-300 w-6 flex-shrink-0">{s.num}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-4">
                    <h3 className={`font-serif text-2xl md:text-3xl font-bold transition-colors duration-300 ${hovered === i ? 'text-[#ec3145]' : 'text-[#1C1C1C]'}`}>{s.title}</h3>
                    <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#1C1C1C]/30 hidden sm:block">{s.brand}</span>
                  </div>
                  <p className={`text-sm text-[#1C1C1C]/50 leading-relaxed mt-1 transition-all duration-300 overflow-hidden ${hovered === i ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'}`}>{s.desc}</p>
                </div>
                <i className={`ri-arrow-right-line text-lg transition-all duration-300 flex-shrink-0 ${hovered === i ? 'text-[#ec3145] translate-x-1' : 'text-[#1C1C1C]/20'}`}></i>
              </Link>
            ))}
          </div>
          <div className="reveal opacity-0 hidden lg:block sticky top-28">
            <div className="relative aspect-[4/3] overflow-hidden">
              {services.map((s, i) => (
                <img key={i} src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500" style={{ opacity: activeImg === i ? 1 : 0 }} />
              ))}
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/50 to-transparent">
                <div className="text-white/60 text-[10px] font-bold tracking-[0.3em] uppercase">{services[activeImg]?.brand}</div>
                <div className="text-white font-serif text-xl font-bold">{services[activeImg]?.title}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
